var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _SearchSpace_activeGraph;
import RBush from "rbush";
import { Waypoint } from "./Waypoint.js";
import { GeometryUtils as G } from "../utils/Geometry.js";
import { Barrier } from "./Barrier.js";
import { V } from "../misc/Vector.js";
import { vec2 } from "gl-matrix";
import { Intersects } from "../utils/Intersects.js";
import Flatten from "@flatten-js/core";
import { RTreeID } from "./RTreeID.js";
import { FullTrajectory } from "./FullTrajectory.js";
import { EndTrajectory } from "./EndTrajectory.js";
import { Orbit } from "./Orbit.js";
import { OrbitConnector } from "./OrbitConnector.js";
import * as cdt2d from 'cdt2d';
export { SearchSpace };
class SearchSpace {
    constructor({ barriers, visibility, waypoints, waypointsTree, activeGraph }) {
        _SearchSpace_activeGraph.set(this, void 0);
        this.waypoints = waypoints !== null && waypoints !== void 0 ? waypoints : new Map();
        this.waypointsTree = waypointsTree !== null && waypointsTree !== void 0 ? waypointsTree : new RBush();
        this.barriers = barriers !== null && barriers !== void 0 ? barriers : new RBush();
        this.visibility = visibility !== null && visibility !== void 0 ? visibility : new Map();
        this.activeWaypoints = new Map();
        __classPrivateFieldSet(this, _SearchSpace_activeGraph, activeGraph, "f");
    }
    set activeGraph(activeGraph) {
        __classPrivateFieldSet(this, _SearchSpace_activeGraph, activeGraph, "f");
        this.activeWaypoints.clear();
        if (__classPrivateFieldGet(this, _SearchSpace_activeGraph, "f") === undefined) {
            this.activeWaypoints = new Map([...this.waypoints]);
        }
        else {
            this.activeWaypoints = new Map([...this.waypoints, ...__classPrivateFieldGet(this, _SearchSpace_activeGraph, "f").waypoints]);
        }
    }
    get activeGraph() {
        return __classPrivateFieldGet(this, _SearchSpace_activeGraph, "f");
    }
    findPortWaypoints({ port, connection }) {
        const minDistance = connection.minBendradius;
        const candidates = [...this.activeWaypoints.values()].map(waypoint => {
            const vector = [waypoint.position[0] - port.position[0], waypoint.position[1] - port.position[1]];
            const distance = Math.hypot(vector[0], vector[1]);
            const dot = Math.abs(vec2.dot(vector, port.direction));
            return {
                waypoint,
                vector,
                distance,
                dot
            };
        });
        return candidates.filter(c => Math.abs(c.dot) < 1e-6 && c.distance + 1e-6 >= minDistance);
    }
    findConnectableOrbitIndices({ current, waypoint, connection }) {
        const virtualRadii = waypoint.trajectories.map((trajectory, index, trajectories) => {
            if (index === 0) {
                return 0.5 * trajectory.radius;
            }
            else {
                return 0.5 * (trajectories[index - 1].radius + trajectory.radius);
            }
        });
        virtualRadii.push(waypoint.trajectories.length === 0 ? connection.minBendradius : waypoint.trajectories[waypoint.trajectories.length - 1].radius + connection.minBendradius);
        const virtualOrbits = virtualRadii.map(radius => new Orbit({
            waypoint: waypoint,
            center: waypoint.position,
            radius,
            width: connection.width,
            spacing: connection.spacing
        }));
        const candidateIndices = virtualOrbits.map((virtualOrbit, index) => {
            const tangents = OrbitConnector.withFixedStartPoint({
                startOrbit: current.candidateTrajectory,
                startRotation: current.candidateTrajectory.rotation,
                targetOrbit: virtualOrbit
            });
            const validTangents = tangents.filter(t => !waypoint.segmentIntersectsTrajectories(t.startPoint, t.endPoint));
            return {
                index,
                tangents: validTangents
            };
        });
        return candidateIndices.filter(candidate => candidate.tangents.length !== 0).map(candidate => candidate.index);
    }
    intersectsBarriers(pointA, pointB) {
        const box = G.boundingBox([pointA, pointB]);
        const candidates = this.barriers.search(box);
        if (candidates.some(c => Intersects.segmentSegment(pointA, pointB, c.points[0], c.points[1], false, false, false))) {
            return true;
        }
        return false;
    }
    intersectsTangents(pointA, pointB) {
        const activeGraph = this.activeGraph;
        if (activeGraph === undefined || V.equals(pointA, pointB)) {
            return false;
        }
        const box = G.boundingBox([pointA, pointB]);
        const candidates = activeGraph.tangentsTree.search(box);
        const tangents = candidates.map(c => activeGraph.tangents.get(c.id));
        if (tangents.some(t => {
            if (t === undefined) {
                throw new Error('Tangent not found');
            }
            return Intersects.segmentSegment(pointA, pointB, t.sourcePoint, t.targetPoint, true, true, false);
        })) {
            return true;
        }
        return false;
    }
    intersectsWaypointTrajectories(waypoint, pointA, pointB) {
        const activeGraph = this.activeGraph;
        if (activeGraph === undefined || V.equals(pointA, pointB)) {
            return false;
        }
        return waypoint.segmentIntersectsTrajectories(pointA, pointB);
    }
    waypointOverlapsBarrier(waypoint) {
        const candidates = this.barriers.search(waypoint);
        return candidates.some(c => waypoint.isIntersectingObstacleSegment(c.points[0], c.points[1]));
    }
    waypointOverlapsTangent(waypoint, width, spacing, exclude) {
        const activeGraph = this.activeGraph;
        if (activeGraph === undefined) {
            return undefined;
        }
        const candidates = activeGraph.tangentsTree.search(waypoint);
        return candidates.some(c => {
            const tangent = activeGraph.tangents.get(c.id);
            if (tangent === undefined) {
                throw new Error('Unexpected case');
            }
            if (exclude.includes(tangent.id)) {
                return false;
            }
            //TODO supply tangents channel data!!!
            return waypoint.isIntersectingSegment(tangent.sourcePoint, tangent.targetPoint, width, spacing);
        });
    }
    waypointOverlapsWaypoint(waypoint) {
        const EPSILON = 1e-3;
        const activeGraph = this.activeGraph;
        if (activeGraph === undefined) {
            return undefined;
        }
        const candidates = [...activeGraph.waypointsTree.search(waypoint), ...this.waypointsTree.search(waypoint)].map(r => { var _a; return (_a = activeGraph.waypoints.get(r.id)) !== null && _a !== void 0 ? _a : this.waypoints.get(r.id); });
        return candidates.some(c => {
            if (c === undefined) {
                throw new Error('Unexpected case');
            }
            if (c.id === waypoint.id) {
                return false;
            }
            if (c.trajectories.some(t => {
                if (!(t instanceof FullTrajectory || t instanceof EndTrajectory)) {
                    return false;
                }
                const arc = t.getArc();
                return waypoint.trajectories.some(wt => {
                    if (!(wt instanceof FullTrajectory || wt instanceof EndTrajectory)) {
                        return false;
                    }
                    if (wt instanceof FullTrajectory && (wt.tangents[0].getOtherTrajectory(wt).waypoint.id === t.waypoint.id || wt.tangents[1].getOtherTrajectory(wt).waypoint.id === t.waypoint.id)) {
                        return false;
                    }
                    if (wt instanceof EndTrajectory && wt.tangent.getOtherTrajectory(wt).waypoint.id === t.waypoint.id) {
                        return false;
                    }
                    const distance = arc.distanceTo(wt.getArc());
                    const marginDistance = distance[0] + EPSILON;
                    // TODO: possible bug! only one-sided spacing considered!
                    return marginDistance < wt.widthHalf + t.widthHalf + t.spacing || marginDistance < wt.widthHalf + t.widthHalf + wt.spacing;
                });
            })) {
                return true;
            }
            return waypoint.trajectories.some(wt => {
                if (!(wt instanceof FullTrajectory || wt instanceof EndTrajectory)) {
                    return false;
                }
                if (wt instanceof EndTrajectory && V.distance(wt.point, c.position) - wt.widthHalf - wt.spacing < EPSILON) {
                    return false;
                }
                const distance = wt.getArc().distanceTo(new Flatten.Point(c.position[0], c.position[1]));
                const marginDistance = distance[0] + EPSILON;
                return marginDistance < c.minDistance + wt.widthHalf || marginDistance < wt.widthHalf + c.innerSpacingFactor * wt.spacing;
            });
        });
    }
    arcIntersectsBarriers(arc) {
        const box = arc.box;
        const candidates = this.barriers.search({
            minX: box.xmin,
            maxX: box.xmax,
            minY: box.ymin,
            maxY: box.ymax,
        });
        if (arc instanceof Flatten.Arc) {
            return candidates.some(c => arc.intersect(c.getSegment()).length !== 0);
        }
        else {
            return false;
        }
    }
    arcIntersectsWaypointTrajectories(arc, waypoint, exclude) {
        if (arc instanceof Flatten.Arc) {
            return waypoint.arcIntersectsTrajectories(arc, exclude);
        }
        else {
            return false;
        }
    }
    //TODO:
    influencingWaypoint(pointA, pointB, width, spacing, exclude) {
        const activeGraph = this.activeGraph;
        if (activeGraph === undefined) {
            return undefined;
        }
        const segment = new Flatten.Segment(new Flatten.Point(pointA[0], pointA[1]), new Flatten.Point(pointB[0], pointB[1]));
        const box = G.boundingBox(G.segmentWithWidth(pointA, pointB, width + 2 * spacing));
        const candidates = [...activeGraph.waypointsTree.search(box), ...this.waypointsTree.search(box)].map(r => { var _a; return (_a = activeGraph.waypoints.get(r.id)) !== null && _a !== void 0 ? _a : this.waypoints.get(r.id); });
        const c = candidates.map(w => {
            if (w === undefined) {
                throw new Error('Invalid WaypointID');
            }
            if (!exclude.includes(w.id) && w.isIntersectingSegment(pointA, pointB, width, spacing)) {
                const distance = w.distanceOnSegment(pointA, pointB);
                if (distance === undefined) {
                    return undefined;
                }
                return {
                    waypoint: w,
                    distance,
                };
            }
            else {
                return undefined;
            }
        }).filter(o => o !== undefined).sort((a, b) => a.distance - b.distance);
        return c.length > 0 ? c[0].waypoint : undefined;
    }
    /*influencingWaypoint(pointA: Point, pointB: Point, width: number, spacing: number, exclude: WaypointID[]) {
      const activeGraph = this.activeGraph
      if(activeGraph === undefined) {
        return undefined
      }
  
      const box = G.boundingBox(G.segmentWithWidth(pointA, pointB, width + 2 * spacing))
      const candidates = [...activeGraph.waypointsTree.search(box), ...this.waypointsTree.search(box)].map(r => activeGraph.waypoints.get(r.id) ?? this.waypoints.get(r.id))
      const c = candidates.map(w => {
        if(w === undefined) {
          throw new Error('Invalid WaypointID')
        }
        if(!exclude.includes(w.id) && w.isIntersectingSegment(pointA, pointB, width, spacing)) {
          const distance = w.distanceOnSegment(pointA, pointB)
          if(distance === undefined) {
            return undefined
          }
          return {
            waypoint: w,
            distance,
          }
        } else {
          return undefined
        }
      }).filter(o => o !== undefined).sort((a, b) => a!.distance - b!.distance)
  
      return c.length > 0 ? c[0]!.waypoint : undefined
    }*/
    static getWaypointsTree(waypoints) {
        const waypointsTree = new RBush();
        waypoints.forEach(waypoint => {
            waypointsTree.insert(new RTreeID({
                id: waypoint.id,
                minX: waypoint.minX,
                maxX: waypoint.maxX,
                minY: waypoint.minY,
                maxY: waypoint.maxY
            }));
        });
        return waypointsTree;
    }
    static getConnectionWaypoints(connections) {
        const waypoints = [];
        for (let connection of connections) {
            //const offset = connection.minBendradius
            const offset = Math.max(connection.minBendradius, 0.5 * connection.width + connection.spacing);
            for (let port of connection.ports) {
                const leftWaypoint = new Waypoint({
                    position: port.leftOffset(offset),
                    minDistance: 0,
                    innerSpacingFactor: 1
                });
                waypoints.push(leftWaypoint);
                const rightWaypoint = new Waypoint({
                    position: port.rightOffset(offset),
                    minDistance: 0,
                    innerSpacingFactor: 1
                });
                waypoints.push(rightWaypoint);
            }
        }
        return waypoints;
    }
    static getConnectionWaypointMutualVisibilities(portWaypoints) {
        const visibility = new Map();
        for (let wA of portWaypoints) {
            for (let wB of portWaypoints) {
                if (wA === wB) {
                    continue;
                }
                let vA = visibility.get(wA.id);
                if (vA === undefined) {
                    visibility.set(wA.id, []);
                }
                vA = visibility.get(wA.id);
                if (!vA.includes(wB.id)) {
                    vA.push(wB.id);
                }
            }
        }
        return visibility;
    }
    static getVisibilityTriangulation(waypoints) {
        const ws = [...waypoints];
        const vertices = ws.map(w => w.position);
        const visibility = new Map();
        const cells = cdt2d(vertices, [], {
            delaunay: true
        });
        cells.forEach(cell => {
            const w0 = ws[cell[0]];
            const w1 = ws[cell[1]];
            const w2 = ws[cell[2]];
            if (w0 === undefined || w1 === undefined || w2 === undefined) {
                throw new Error('Triangulation failed');
            }
            if (!visibility.has(w0.id)) {
                visibility.set(w0.id, []);
            }
            if (!visibility.get(w0.id).includes(w1.id)) {
                visibility.get(w0.id).push(w1.id);
            }
            if (!visibility.get(w0.id).includes(w2.id)) {
                visibility.get(w0.id).push(w2.id);
            }
            if (!visibility.has(w1.id)) {
                visibility.set(w1.id, []);
            }
            if (!visibility.get(w1.id).includes(w0.id)) {
                visibility.get(w1.id).push(w0.id);
            }
            if (!visibility.get(w1.id).includes(w2.id)) {
                visibility.get(w1.id).push(w2.id);
            }
            if (!visibility.has(w2.id)) {
                visibility.set(w2.id, []);
            }
            if (!visibility.get(w2.id).includes(w1.id)) {
                visibility.get(w2.id).push(w1.id);
            }
            if (!visibility.get(w2.id).includes(w0.id)) {
                visibility.get(w2.id).push(w0.id);
            }
        });
        return visibility;
    }
    static getVisibilityTriangulationMultiStep(waypoints, steps) {
        const visibility = SearchSpace.getVisibilityTriangulation(waypoints);
        const numSteps = Math.round(steps);
        if (numSteps <= 1) {
            return visibility;
        }
        if (numSteps > 2) {
            throw new Error('To be implemented');
        }
        const steppedVisibility = new Map();
        [...visibility.keys()].forEach(waypointID => steppedVisibility.set(waypointID, []));
        [...visibility.entries()].forEach(([waypointID, waypoints]) => {
            const neighbors = steppedVisibility.get(waypointID);
            neighbors.push(...waypoints);
            [...waypoints].forEach(waypoint => {
                const secondNeighbors = visibility.get(waypoint);
                secondNeighbors.forEach(sn => {
                    if (!neighbors.includes(sn) && sn !== waypointID) {
                        neighbors.push(sn);
                    }
                });
            });
        });
        return steppedVisibility;
    }
    static getTotalVisibility(waypoints) {
        const visibility = new Map();
        waypoints.forEach(w => {
            visibility.set(w.id, [...waypoints].filter(n => n.id !== w.id).map(n => n.id));
        });
        return visibility;
    }
    static mergeVisibilities(mapA, mapB) {
        const visibility = new Map();
        mapA.forEach((value, key) => {
            visibility.set(key, [...value]);
        });
        mapB.forEach((value, key) => {
            const v = visibility.get(key);
            if (v === undefined) {
                visibility.set(key, [...value]);
            }
            else {
                v.push(...value);
            }
        });
        return visibility;
    }
    static ofWaypoints({ waypoints, obstacles }) {
        //TODO obstacles
        const waypointMap = new Map();
        waypoints.forEach(w => {
            waypointMap.set(w.id, w);
        });
        const leafEntries = 9;
        const barriers = new RBush(leafEntries);
        for (let o of obstacles) {
            if (o.points.length < 3) {
                throw new Error('Invalid obstacle');
            }
            const polygon = G.polygonIsClockwise(o.points) ? o.points : [...o.points].reverse();
            const transformed = polygon.reduce((a, c) => {
                a.barriers.push(new Barrier({ points: [a.point, c], normal: G.leftOrthogonal([c[0] - a.point[0], c[1] - a.point[1]]) }));
                a.point = c;
                return a;
            }, { point: polygon[polygon.length - 1], barriers: [] });
            barriers.load(transformed.barriers);
        }
        const waypointsTree = SearchSpace.getWaypointsTree(waypoints);
        //const visibility = SearchSpace.getVisibilityTriangulationMultiStep(waypoints, 2)
        const visibility = SearchSpace.getTotalVisibility(waypoints);
        return new SearchSpace({
            waypoints: waypointMap,
            waypointsTree,
            visibility,
            barriers
        });
    }
    static of({ connections, obstacles }) {
        const leafEntries = 9;
        const c = {
            waypoints: new Map(),
            waypointsTree: new RBush(leafEntries),
            visibility: new Map(),
            barriers: new RBush(leafEntries),
        };
        for (let o of obstacles) {
            if (o.points.length < 3) {
                throw new Error('Invalid obstacle');
            }
            const polygon = G.polygonIsClockwise(o.points) ? o.points : [...o.points].reverse();
            const transformed = polygon.reduce((a, c) => {
                a.barriers.push(new Barrier({ points: [a.point, c], normal: G.leftOrthogonal([c[0] - a.point[0], c[1] - a.point[1]]) }));
                a.point = c;
                return a;
            }, { point: polygon[polygon.length - 1], barriers: [] });
            c.barriers.load(transformed.barriers);
            for (let i = 0; i < o.points.length; i++) {
                const previous = i === 0 ? o.points[o.points.length - 1] : o.points[i];
                const current = o.points[i];
                const next = i === o.points.length - 1 ? o.points[0] : o.points[i];
                const waypoint = new Waypoint({
                    position: current,
                    minDistance: 0,
                    innerSpacingFactor: 1
                });
                c.waypoints.set(waypoint.id, waypoint);
            }
        }
        const connectionWaypoints = SearchSpace.getConnectionWaypoints(connections);
        connectionWaypoints.forEach(w => {
            c.waypoints.set(w.id, w);
        });
        const triangulationVisibility = SearchSpace.getVisibilityTriangulationMultiStep([...c.waypoints.values()], 2);
        const connectionWaypointVisibility = SearchSpace.getConnectionWaypointMutualVisibilities(connectionWaypoints);
        c.visibility = SearchSpace.mergeVisibilities(triangulationVisibility, connectionWaypointVisibility);
        c.waypointsTree = SearchSpace.getWaypointsTree([...c.waypoints.values()]);
        return new SearchSpace(c);
    }
    withAdditionalWaypointsUnique({ waypoints }) {
        return this.withAdditionalWaypoints({
            waypoints: waypoints.filter(w => !this.waypoints.has(w.id))
        });
    }
    withAdditionalWaypoints({ waypoints }) {
        const s = {
            waypoints: new Map(),
            waypointsTree: new RBush(),
            visibility: new Map(),
            barriers: this.barriers,
        };
        this.waypoints.forEach(w => {
            s.waypoints.set(w.id, w);
        });
        waypoints.forEach(w => {
            s.waypoints.set(w.id, w);
        });
        s.visibility = SearchSpace.getVisibilityTriangulationMultiStep([...s.waypoints.values()], 2);
        s.waypointsTree = SearchSpace.getWaypointsTree([...s.waypoints.values()]);
        return new SearchSpace(s);
    }
}
_SearchSpace_activeGraph = new WeakMap();
//# sourceMappingURL=SearchSpace.js.map